import React, { useEffect } from 'react';
import {
  createFilterOptions,
  Autocomplete,
  CircularProgress,
  TextField,
  MenuItem,
  type AutocompleteRenderInputParams,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import { sendApiRequest } from 'lib/api/public';
import { FieldError } from 'react-hook-form';
import ClientForm from 'components/client/ClientForm';
import { accentActionSx, missingEmailPrefixStyle } from 'components/common/utils';
import { getTourAutocompleteSlotProps } from 'components/onboarding/tourMenuStacking';

interface Client {
  _id?: string;
  name: string;
  isAddOption?: boolean;
  /** Computed boolean returned by /clients/list for every row; locally built options may omit it. */
  hasInvoiceEmail?: boolean;
}

type ClientsApiResponse = {
  list: Client[];
};

type AutocompleteService = {
  getClientsApiMethod: (search: string) => Promise<ClientsApiResponse>;
};

const filter = createFilterOptions<Client>();
const autocompleteService: { current: AutocompleteService | null } = { current: null };

export type MissingEmailContext = 'invoices';

const getMissingEmailPrefix = (client: Client, context?: MissingEmailContext) => {
  if (context === 'invoices' && client.hasInvoiceEmail === false) {
    return '(missing email for Invoices)';
  }

  return '';
};

const getClientsApiMethod = (search: string) => {
  return sendApiRequest<ClientsApiResponse>({ path: `/clients/list?search=${search}`, method: 'get' });
};

type Props = {
  onSelect: (client: Client | null) => void;
  disabled?: boolean;
  error?: FieldError;
  defaultValue?: Client;
  testId?: string;
  autocompleteSlotProps?: Record<string, unknown>;
  /** When set, annotates options whose saved invoice address is blank. */
  missingEmailContext?: MissingEmailContext;
  /** When set, raises popper z-index only while this onboarding form tour is active. */
  tourMenuTaskId?: string;
};

export default function ClientsAutoComplete({
  onSelect,
  disabled = false,
  error,
  defaultValue,
  testId,
  autocompleteSlotProps,
  missingEmailContext,
  tourMenuTaskId,
}: Props) {
  const tourAutocompleteSlotProps = getTourAutocompleteSlotProps(tourMenuTaskId);
  const [loading, setLoading] = React.useState(false);
  const [value, setValue] = React.useState<Client | null>(defaultValue || null);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState<Client[]>([]);
  const [showClientForm, setShowClientForm] = React.useState(false);

  const fetch = React.useMemo(
    () =>
      debounce((request: { input: string }, callback: (results?: Client[]) => void) => {
        setLoading(true);
        const service = autocompleteService.current;

        if (!service) {
          setLoading(false);
          callback([]);
          return;
        }

        service
          .getClientsApiMethod(request.input)
          .then((data: ClientsApiResponse) => {
            setLoading(false);
            callback(data.list || []);
          })
          .catch(error => {
            console.log('error', error);

            setLoading(false);
          });
      }, 400),
    [],
  );

  const setOptionsFromFetch = (results: Client[]) => {
    let newOptions: Client[] = [];

    if (value) {
      newOptions = [value];
    }

    if (results) {
      newOptions = [...newOptions, ...results];
    }

    setOptions(newOptions);
  };

  useEffect(() => {
    if (defaultValue && (!options || options.length === 0)) {
      setOptionsFromFetch([defaultValue]);
    }
  }, [defaultValue]);

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current) {
      autocompleteService.current = {
        getClientsApiMethod,
      };
    }

    fetch({ input: inputValue }, (results?: Client[]) => {
      if (active) {
        setOptionsFromFetch(results || []);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  const renderForm = () => {
    if (!showClientForm) {
      return null;
    }

    return (
      <ClientForm
        onClose={() => setShowClientForm(false)}
        open={showClientForm}
        onSuccess={response => {
          if (!response) {
            setShowClientForm(false);
            return;
          }

          const newValue = {
            _id: response._id,
            name: response.name,
          };

          setOptions(newValue ? [newValue, ...options] : options);
          setValue(newValue);
          onSelect(newValue);

          setShowClientForm(false);
        }}
      />
    );
  };

  const renderInput = React.useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        id="clients-auto-complete"
        error={!!error}
        helperText={error?.message}
        size="small"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <React.Fragment>
              {loading ? <CircularProgress color="inherit" size={20} /> : null}
              {params.InputProps.endAdornment}
            </React.Fragment>
          ),
        }}
      />
    ),
    [loading, error],
  );

  return (
    <>
      {renderForm()}

      <Autocomplete
        isOptionEqualToValue={(option, value) => option.name === value.name}
        defaultValue={defaultValue}
        size="small"
        getOptionLabel={option => option.name || ''}
        getOptionKey={option => option._id || option.name}
        options={options}
        autoComplete
        disabled={disabled}
        slotProps={{
          ...tourAutocompleteSlotProps,
          ...autocompleteSlotProps,
        }}
        data-testid={testId}
        includeInputInList
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          const isExisting = options.some(option => inputValue === option.name);

          if (!isExisting) {
            filtered.unshift({
              name: 'Create a new client ...',
              isAddOption: true,
            });
          }

          return filtered;
        }}
        filterSelectedOptions
        value={value}
        onChange={(_event, newValue: Client | null) => {
          if (newValue && newValue.name === 'Create a new client ...') {
            setShowClientForm(true);
            return;
          }

          setOptions(newValue ? [newValue, ...options] : options);
          setValue(newValue);

          onSelect(newValue);
        }}
        onInputChange={(_event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderInput={renderInput}
        renderOption={(props, option) => {
          const { key, ...restProps } = props as typeof props & { key: React.Key };
          if (option.isAddOption) {
            return (
              <MenuItem key={key} {...restProps} sx={accentActionSx}>
                + Add new Client
              </MenuItem>
            );
          }

          return (
            <li key={key} {...restProps}>
              {option.name}
              {getMissingEmailPrefix(option, missingEmailContext) ? (
                <span style={missingEmailPrefixStyle}>{getMissingEmailPrefix(option, missingEmailContext)}</span>
              ) : null}
            </li>
          );
        }}
      />
    </>
  );
}
